







import React, { useState, useEffect } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useParams
} from 'react-router-dom';

import Sidebar from './components/SideBar';
import BannerPage from './Pages/BannerPage';
import ProductManager from './Pages/ProductManager';
import SpinWheel from './Pages/SpinWheel';
import AdminDashboard from './Pages/AdminDashboard';
import Dashboard from './Pages/Dashboard';
import SKUManager from './Pages/SKUManager';
import Manager from './Pages/stores';
import Reports from './Pages/Reports';
import LoginPage from './Pages/LoginPage';
import CodeRedemption from './Pages/CodeRedemption';
import CampaignStats from './Pages/Campaignstats';
import ThemeManager from './Pages/ThemeManager';
import RedeemCode from './Pages/redeemcode';
import BlockedNumbers from './Pages/BlockedNumbers';

import api from './api/api';

const CampaignEndedPage = CampaignStats;

const getUser = () => {
  try {
    const userData = localStorage.getItem('user');
    return userData ? JSON.parse(userData) : {};
  } catch {
    return {};
  }
};

const ProtectedRoute = ({ children, allowedDesignations }) => {
  const user = getUser();

  if (!user || Object.keys(user).length === 0) {
    return <Navigate to="/login" replace />;
  }

  const designation = user.designation?.trim();

  if (allowedDesignations && !allowedDesignations.includes(designation)) {
    if (designation === 'Admin') {
      return <Navigate to="/stores_detail" replace />;
    }

    if (designation === 'Super Admin') {
      return <Navigate to="/dashboard" replace />;
    }

    if (designation === 'User') {
      return <Navigate to="/reports" replace />;
    }

    if (designation === 'Store') {
      return <Navigate to="/" replace />;
    }

    return <Navigate to="/login" replace />;
  }

  return children;
};

// ==========================================================================
// 🚀 CAMPAIGN ROUTE WRAPPER
// ==========================================================================
const CampaignRouteWrapper = () => {
  const { id } = useParams();

  const [campaignActive, setCampaignActive] = useState(null);

  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);

  const storeIdFromUrl = searchParams.get('storeId');

  const user = getUser();

  const storeId = id || storeIdFromUrl || user?.shortName || user?.id || '';

  useEffect(() => {
    const verifyCampaign = async () => {
      try {
        console.log(`Sending Store ID to Backend: ${storeId}`);

        const response = await api.get(`/api/campaign/verify?storeId=${storeId}`);

        const data = response.data;

        if (data.success) {
          setCampaignActive(data.isActive);
        } else {
          setCampaignActive(false);
        }
      } catch (error) {
        console.error('Failed to verify campaign:', error);
        setCampaignActive(false);
      }
    };

    verifyCampaign();
  }, [storeId]);

  if (campaignActive === null) {
    return (
      <div className="flex items-center justify-center h-full w-full bg-white min-h-[300px]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#FFD166]"></div>
      </div>
    );
  }

  return campaignActive ? <SpinWheel /> : <CampaignEndedPage />;
};

function AppContent() {
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);

  const isCustomerView = searchParams.get('view') === 'customer';

  const isLoginPage = location.pathname === '/login';

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      {!isCustomerView && !isLoginPage && <Sidebar />}

      <div className="flex-1 flex flex-col overflow-hidden">
        {!isLoginPage && <BannerPage isGlobalOnly={true} />}

        <main
          className={`
            flex-1
            overflow-y-auto
            bg-white
            ${isLoginPage ? '' : 'pt-12'}
            ${isCustomerView || isLoginPage ? 'p-0' : 'p-6'}
          `}
        >
          <Routes>
            {/* LOGIN */}
            <Route path="/login" element={<LoginPage />} />

            {/* CUSTOMER / QR ROUTE */}
            <Route path="/wheel/:id" element={<CampaignRouteWrapper />} />

            {/* CAMPAIGN ROUTES */}
            <Route path="/campaign-stats" element={<CampaignStats />} />
            <Route path="/campaign-ended" element={<CampaignEndedPage />} />

            {/* MAIN HOME ROUTE */}
            <Route
              path="/"
              element={
                <ProtectedRoute allowedDesignations={['Admin', 'Store']}>
                  <CampaignRouteWrapper />
                </ProtectedRoute>
              }
            />

            {/* ADMIN ONLY PAGES */}
            <Route
              path="/stores_detail"
              element={
                <ProtectedRoute allowedDesignations={['Admin']}>
                  <Manager />
                </ProtectedRoute>
              }
            />

            <Route
              path="/products"
              element={
                <ProtectedRoute allowedDesignations={['Admin']}>
                  <ProductManager />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin"
              element={
                <ProtectedRoute allowedDesignations={['Admin']}>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />

            <Route
              path="/sku_detail"
              element={
                <ProtectedRoute allowedDesignations={['Admin']}>
                  <SKUManager />
                </ProtectedRoute>
              }
            />

            <Route
              path="/announcements"
              element={
                <ProtectedRoute allowedDesignations={['Admin']}>
                  <BannerPage isGlobalOnly={false} />
                </ProtectedRoute>
              }
            />

            {/* MANAGEMENT DASHBOARD */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute allowedDesignations={['Admin', 'Super Admin']}>
                  <Dashboard />
                </ProtectedRoute>
              }
            />

            {/* REPORTS */}
            <Route
              path="/reports"
              element={
                <ProtectedRoute allowedDesignations={['Admin', 'User']}>
                  <Reports />
                </ProtectedRoute>
              }
            />

            {/* THEME MANAGER */}
            <Route
              path="/theme-manager"
              element={
                <ProtectedRoute allowedDesignations={['Admin']}>
                  <ThemeManager />
                </ProtectedRoute>
              }
            />

            {/* CODE REDEMPTION (STORE QR) */}
            <Route
              path="/code-redemption"
              element={
                <ProtectedRoute allowedDesignations={['Admin', 'Store']}>
                  <CodeRedemption />
                </ProtectedRoute>
              }
            />

            {/* EXCEL REDEMPTION IMPORT PANEL */}
            <Route
              path="/redeemcode"
              element={
                <ProtectedRoute allowedDesignations={['Admin', 'Super Admin', 'Store']}>
                  <RedeemCode />
                </ProtectedRoute>
              }
            />

            {/* BLOCKED NUMBERS ROUTE */}
            <Route
              path="/blocked-numbers"
              element={
                <ProtectedRoute allowedDesignations={['Admin', 'Super Admin', 'Store']}>
                  <BlockedNumbers />
                </ProtectedRoute>
              }
            />

            {/* FALLBACK */}
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;